import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AeroCardMenu from '../../../components/Aero/AeroCardMenu';
import UTDeleteModal from './UTDeleteModal';
import { AeroPreviewTile, AeroSessionDots, AeroStatus } from '../../../components/Aero/AeroHomeParts';
import { sessionDots } from '../../AeroHome/homeModel';
import { buildTestUrl, displayTestUrl } from '../testUrl';
import '../../../styles/aero-ut-theme.css';

const STATUS_LABEL = {
  active: 'Active',
  completed: 'Completed',
  draft: 'Draft',
  analyzing: 'Analyzing',
};

function UTProjectRow({ project, onDelete, onRename, onDuplicate }) {
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleClick = () => {
    navigate(`/user-testing/${project.id}`);
  };

  const handleRename = () => {
    const next = window.prompt('Rename test', project.title || '');
    if (next === null) return;
    const trimmed = next.trim();
    if (!trimmed || trimmed === project.title) return;
    onRename?.(project.id, trimmed);
  };

  const handleDuplicate = () => {
    onDuplicate?.(project.id);
  };

  const handleDeleteMenu = () => {
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!onDelete) return;
    setDeleting(true);
    const result = await onDelete(project.id);
    setDeleting(false);
    if (result?.success) {
      setShowDeleteModal(false);
    }
  };

  // Lowercase so it reads after other words: "Updated yesterday".
  const getRelativeTime = (date) => {
    if (!date) return 'none yet';
    const now = new Date();
    const diff = now - new Date(date);
    const hours = Math.floor(diff / (1000 * 60 * 60));

    if (hours < 1) return 'just now';
    if (hours === 1) return '1 hour ago';
    if (hours < 24) return `${hours} hours ago`;

    const days = Math.floor(hours / 24);
    if (days === 1) return 'yesterday';
    if (days < 7) return `${days} days ago`;
    if (days < 30) {
      const weeks = Math.floor(days / 7);
      return `${weeks} week${weeks === 1 ? '' : 's'} ago`;
    }

    return new Date(date).toLocaleDateString();
  };

  // On its own in the Activity column: "Yesterday", "11 hours ago".
  const sentenceStart = (t) => t.charAt(0).toUpperCase() + t.slice(1);

  const deriveStatus = () => {
    if (project.userTesting?.status === 'completed') return 'completed';
    const target = Number(project.userTesting?.targetSessions) || 0;
    if (target > 0 && (project.sessionCount || 0) >= target) return 'completed';
    if (project.userTesting?.status) return project.userTesting.status;
    if ((project.sessionCount || 0) === 0) return 'draft';
    return 'active';
  };

  const status = deriveStatus();
  // v3 status words: Open (taking sessions), Closed (completed), Draft.
  const statusKind = status === 'active' ? 'open' : status === 'completed' ? 'closed' : 'draft';
  const statusWord = status === 'active' ? 'Open' : status === 'completed' ? 'Closed' : (STATUS_LABEL[status] || status);

  const n = project.sessionCount || 0;
  const sourceLine = project.type === 'html'
    ? `Site, ${project.generatedUrl || ''}.aeroapp.ai`
    : project.customDomain
      ? `Figma prototype on ${project.customDomain}`
      : 'Figma prototype';
  const link = displayTestUrl(buildTestUrl(project));

  return (
    <>
      <div
        className="aero-tests-row"
        onClick={handleClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
          }
        }}
      >
        <AeroPreviewTile
          step="test"
          name={project.title}
          previewUrl={project.previewUrl || null}
          previewColors={project.previewColors || null}
        />
        <span className="aero-tests-row-n">
          <b>{project.title}</b>
          <small>{sourceLine}</small>
        </span>
        <span className="aero-tests-row-link">{link}</span>
        <span className="aero-tests-row-ses">
          <AeroSessionDots dots={sessionDots(project)} />
          <small>{n === 0 ? 'No sessions yet' : `${n} session${n === 1 ? '' : 's'}`}</small>
        </span>
        <span className="aero-tests-row-last">
          {project.lastSessionAt ? sentenceStart(getRelativeTime(project.lastSessionAt)) : `Updated ${getRelativeTime(project.updatedAt || project.createdAt)}`}
        </span>
        <AeroStatus kind={statusKind}>{statusWord}</AeroStatus>
        <span className="aero-tests-row-menu">
          <AeroCardMenu
            items={[
              { label: 'Rename', onClick: handleRename },
              { label: 'Duplicate', onClick: handleDuplicate },
              { label: 'Delete', onClick: handleDeleteMenu, danger: true },
            ]}
          />
        </span>
      </div>

      <UTDeleteModal
        isOpen={showDeleteModal}
        type="project"
        onConfirm={handleConfirmDelete}
        onCancel={() => setShowDeleteModal(false)}
        loading={deleting}
      />
    </>
  );
}

export default UTProjectRow;
